import { useMemo, useState } from "react";

export type DataTableSortDirection = "asc" | "desc";
export type ClientSortValue = string | number | boolean | null | undefined;

type UseClientTableSortOptions<T> = {
  items: T[];
  /**
   * Active column on first paint.
   * Pass `null` to keep the incoming order until the user clicks a header.
   */
  defaultKey: string | null;
  defaultDirection?: DataTableSortDirection;
  getValue: (item: T, key: string) => ClientSortValue;
  initialDirectionForKey?: (key: string) => DataTableSortDirection;
};

function compareValues(left: ClientSortValue, right: ClientSortValue): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  if (typeof left === "boolean" && typeof right === "boolean") {
    return Number(left) - Number(right);
  }
  return String(left).localeCompare(String(right), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

export function useClientTableSort<T>({
  items,
  defaultKey,
  defaultDirection = "asc",
  getValue,
  initialDirectionForKey,
}: UseClientTableSortOptions<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultKey);
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>(defaultDirection);
  const [userSorted, setUserSorted] = useState(defaultKey != null);

  const onSortChange = (key: string) => {
    setUserSorted(true);
    if (key === sortKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(
      initialDirectionForKey?.(key) ??
        (key.endsWith("At") || key === "createdAt" || key === "updatedAt"
          ? "desc"
          : "asc"),
    );
  };

  const sortedItems = useMemo(() => {
    if (!userSorted || !sortKey) return items;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const result = compareValues(getValue(a, sortKey), getValue(b, sortKey));
      if (result !== 0) return dir * result;
      if (defaultKey) {
        return compareValues(getValue(a, defaultKey), getValue(b, defaultKey));
      }
      return 0;
    });
  }, [items, sortKey, sortDirection, getValue, defaultKey, userSorted]);

  return {
    sortedItems,
    sortKey,
    sortDirection,
    onSortChange,
  };
}
